import { useNavigate, Link } from "react-router-dom";
import type { ReactNode } from "react";
import Navbar from "./Navbar";

function GeometricShapes() {
  return (
    <div className="geometric-shapes">
      <div className="geo-shape triangle" />
      <div className="geo-shape diamond" />
      <div className="geo-shape circle" />
    </div>
  );
}

interface FaqLayoutProps {
  breadcrumb: string;
  title: string;
  accent?: string;
  children: ReactNode;
}

export default function FaqLayout({ breadcrumb, title, accent, children }: FaqLayoutProps) {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen">
      <GeometricShapes />
      <div className="relative z-10">
        <Navbar />

        <section className="bg-[#0d0d0d] pt-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto py-12 md:py-20">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#6C7278] font-medium mb-4">{breadcrumb}</p>
            <h1 className="text-3xl md:text-4xl lg:text-[48px] font-extrabold text-white tracking-[-0.02em] leading-tight">
              {title}
              {accent && <span className="block mt-2 text-[#B8422E]">{accent}</span>}
            </h1>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              {children}
            </div>

            <div className="mt-12 pt-8 border-t border-[#E2DED9]">
              <p className="text-sm text-[#6C7278] mb-4">Still need help?</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact-support"
                  className="inline-flex items-center gap-2 bg-[#1A1C1E] text-white text-sm font-medium px-6 py-3 rounded-xl hover:bg-black transition-colors"
                >
                  Contact Support
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/dispute-resolution"
                  className="inline-flex items-center gap-2 border border-[#D1D5DB] text-[#1A1C1E] text-sm font-medium px-6 py-3 rounded-xl hover:border-[#1A1C1E] transition-colors"
                >
                  File a Dispute
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="bg-[#0d0d0d] border-t border-white/[0.06]">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 pb-10 md:pb-12 border-b border-white/[0.06]">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-base font-bold tracking-[0.16em] text-white uppercase">Trevionix Help</span>
                  <span className="w-px h-4 bg-white/20"></span>
                  <span className="text-[11px] tracking-[0.12em] text-[#6C7278] uppercase">LTD</span>
                </div>
                <p className="text-sm text-[#6C7278] max-w-[260px] leading-relaxed">
                  12 Constance Street<br />
                  London, E16 2DQ<br />
                  United Kingdom
                </p>
              </div>
            </div>
            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[#6C7278]">&copy; 2026 Trevionix LTD. All rights reserved.</p>
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-xs text-[#6C7278] hover:text-white transition-colors"
              >
                <i className="ri-arrow-left-line" />
                Back to Help Center
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}